import { LogManager } from '@src/utility/Logger';
import {
  buildSpeakersFromMappings,
  extractSpeakerLabelsFromTranscriptElements,
  fetchDatoSpeakers,
  fetchSpeakerVoiceMappings,
} from '@src/utility/speakerUtils';

import type { Speaker } from '@src/components/content-components/SpeakerTypes';
import type { DatoClientStore } from '@src/stores-v2/DatoClientStore';
import type { TranscriptElementWithClipSegmentAttributes } from '@src/types.ts/transcript';

export interface DatoSpeakerLite {
  id: string;
  name?: string;
  title?: string;
  voice?: string;
  bio?: string;
}

export interface SpeakerLookupResult {
  transcriptElement: TranscriptElementWithClipSegmentAttributes | null;
  speaker: DatoSpeakerLite | null;
}

/**
 * Service providing time-based speaker resolution from transcript data.
 * Starts with a simple linear scan; can be upgraded to cached binary search later.
 */
export class TranscriptSpeakerService {
  // Cached sorted start times + elements array identity to avoid rebuilding each lookup.
  private _cacheElementsRef:
    | TranscriptElementWithClipSegmentAttributes[]
    | null = null;
  private _cacheStartTimes: number[] = [];
  private _cacheFiltered: Array<
    TranscriptElementWithClipSegmentAttributes & { ts: number; end_ts: number }
  > = [];
  private _cacheValid = false;

  private rebuildIndex(elements: TranscriptElementWithClipSegmentAttributes[]) {
    this._cacheStartTimes = [];
    this._cacheFiltered = [];
    for (let i = 0; i < elements.length; i++) {
      const e = elements[i];
      if (typeof e.ts === 'number' && typeof e.end_ts === 'number') {
        const timed: TranscriptElementWithClipSegmentAttributes & {
          ts: number;
          end_ts: number;
        } = { ...e, ts: e.ts, end_ts: e.end_ts };
        this._cacheFiltered.push(timed);
        this._cacheStartTimes.push(timed.ts);
      }
    }
    this._cacheValid = true;
  }

  /**
   * O(log n) lookup using binary search over element start times.
   * Semantics preserved: match if ts <= t < end_ts, otherwise null.
   */
  getSpeakerAtTime(
    transcriptElements:
      | TranscriptElementWithClipSegmentAttributes[]
      | null
      | undefined,
    timeSeconds: number,
    speakerById: Record<string, DatoSpeakerLite> | null | undefined,
  ): SpeakerLookupResult {
    if (
      !Array.isArray(transcriptElements) ||
      timeSeconds < 0 ||
      transcriptElements.length === 0
    ) {
      return { transcriptElement: null, speaker: null };
    }

    // Rebuild index if underlying reference changed or invalidated.
    if (this._cacheElementsRef !== transcriptElements) {
      this._cacheElementsRef = transcriptElements;
      this.rebuildIndex(transcriptElements);
    } else if (!this._cacheValid) {
      this.rebuildIndex(transcriptElements);
    }

    const starts = this._cacheStartTimes;
    // Binary search for rightmost start <= timeSeconds
    let lo = 0;
    let hi = starts.length - 1;
    let candidate = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const s = starts[mid];
      if (s <= timeSeconds) {
        candidate = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }

    if (candidate === -1) {
      return { transcriptElement: null, speaker: null };
    }

    // Walk left in filtered array of only timed elements
    for (let i = candidate; i >= 0; i--) {
      const el = this._cacheFiltered[i];
      if (!el) continue;
      if (el.ts <= timeSeconds && timeSeconds < el.end_ts) {
        const internal = el.speaker_label;
        const speaker = internal ? speakerById?.[internal] ?? null : null;
        return { transcriptElement: el, speaker };
      }
    }
    return { transcriptElement: null, speaker: null };
  }

  /**
   * High-level orchestration: given a story id, current time, transcript elements provider, and a dato client store,
   * fetch mapping + speakers, build index, and return lookup result plus auxiliary data for callers.
   */
  async resolveSpeakerAtTime(params: {
    storyId: string | undefined;
    timeSeconds: number;
    getTranscriptElements: () =>
      | TranscriptElementWithClipSegmentAttributes[]
      | null
      | undefined;
    datoClientStore: DatoClientStore | null;
  }): Promise<
    | (SpeakerLookupResult & {
        speakersIndex: Record<
          string,
          { speakerLabel?: string; name?: string; title?: string }
        >;
        availableSpeakers: {
          speakerLabel?: string;
          name?: string;
          title?: string;
        }[];
      })
    | null
  > {
    const { storyId, timeSeconds, getTranscriptElements, datoClientStore } =
      params;
    if (!storyId || typeof timeSeconds !== 'number') return null;
    try {
      const transcriptElements = getTranscriptElements();
      if (!transcriptElements || transcriptElements.length === 0) return null;

      const speakerMappingsPromise = fetchSpeakerVoiceMappings(storyId);
      const datoSpeakersPromise = datoClientStore
        ? fetchDatoSpeakers(storyId, datoClientStore)
        : Promise.resolve([]);
      const [speakerMappings, datoSpeakers] = await Promise.all([
        speakerMappingsPromise,
        datoSpeakersPromise,
      ]);

      const labels =
        extractSpeakerLabelsFromTranscriptElements(transcriptElements);
      if (!labels.length) return null;
      const builtSpeakersRaw = buildSpeakersFromMappings(
        labels,
        speakerMappings,
        datoSpeakers,
      );
      type MinimalSpeaker = {
        speakerLabel?: string;
        name?: string;
        title?: string;
      };
      const sanitizedSpeakers: MinimalSpeaker[] = [];
      const isSpeaker = (val: unknown): val is Speaker =>
        !!val && typeof val === 'object' && 'speakerLabel' in val;
      if (Array.isArray(builtSpeakersRaw)) {
        for (const item of builtSpeakersRaw) {
          if (!isSpeaker(item)) continue;
          const name =
            typeof item.name === 'string' && item.name.length > 0
              ? item.name
              : undefined;
          const title =
            typeof item.title === 'string' && item.title.length > 0
              ? item.title
              : undefined;
          // item.speakerLabel guaranteed string by Speaker type
          sanitizedSpeakers.push({
            speakerLabel: item.speakerLabel,
            name,
            title,
          });
        }
      }
      const speakersIndex = sanitizedSpeakers.reduce<
        Record<string, MinimalSpeaker>
      >((acc, s) => {
        if (s.speakerLabel) acc[s.speakerLabel] = s;
        return acc;
      }, {});

      // Adapt index into expected map type (ids correlate with speakerLabel for now)
      // Map MinimalSpeaker to DatoSpeakerLite shape (id fallback to label when missing)
      const speakerMap: Record<string, DatoSpeakerLite> = {};
      for (const [k, v] of Object.entries(speakersIndex)) {
        speakerMap[k] = { id: k, name: v.name, title: v.title };
      }
      const lookup = this.getSpeakerAtTime(
        transcriptElements,
        timeSeconds,
        speakerMap,
      );
      return { ...lookup, speakersIndex, availableSpeakers: sanitizedSpeakers };
    } catch (error) {
      LogManager.getLogger('service:TranscriptSpeakerService').warn(
        'resolveSpeakerAtTime failed',
        { error },
      );
      return null;
    }
  }
}

export const transcriptSpeakerService = new TranscriptSpeakerService();
